import React, { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { adminMealsAPI } from '../../services/meals.api';
import { CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import StarIcon from '@mui/icons-material/Star';
import FastfoodIcon from '@mui/icons-material/Fastfood';

// Material UI imports
import {
  Container,
  Pagination,
  Grid,
  Paper,
  Typography,
  Box,
  TextField,
  Button,
  IconButton,
  Card,
  CardContent,
  CardActions,
  CardHeader,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Select,
  MenuItem,
  InputLabel,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  Alert,
  Avatar,
  useTheme,
  alpha,
  Stack,
  CardMedia,
  Snackbar
} from '@mui/material';

// Icons
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Fastfood,
  RestaurantMenu,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Spa,
  LocalDining,
  EmojiFoodBeverage,
  Info as InfoIcon
} from '@mui/icons-material';

const schema = yup.object({
  name: yup.string().required('Meal name is required'),
  description: yup.string(),
  mealType: yup.string().required('Meal type is required'),
  dietaryType: yup.array().of(yup.string()).min(1, 'Select at least one dietary type'),
  cuisineStyle: yup.array().of(yup.string()),
  tags: yup.array().of(yup.string()),
  seasonality: yup.array().of(yup.string()),
  festivalTags: yup.array().of(yup.string()),
  imageUrl: yup.string().nullable().transform(value => value === '' ? null : value)
});

// Helper function to get meal type icon
const getMealTypeIcon = (type) => {
  switch(type?.toLowerCase()) {
    case 'breakfast': return <EmojiFoodBeverage />;
    case 'lunch': return <LocalDining />;
    case 'dinner': return <RestaurantMenu />;
    default: return <Fastfood />;
  }
};

const getMealIcon = (type) => {
  switch(type) {
    case 'Daily':
      return <RestaurantIcon titleAccess="Daily Meal" />;
    case 'Special':
      return <StarIcon titleAccess="Special Meal" color="warning" />;
    default:
      return <FastfoodIcon titleAccess="Other Meal" />;
  }
};

// Helper function to get dietary type icon
const getDietaryTypeIcon = (type) => {
  switch(type?.toLowerCase()) {
    case 'vegetarian': return <Spa />;
    case 'veg': return <Spa />;
    case 'non-vegetarian': return <Fastfood />;
    case 'non-veg': return <Fastfood />;
    default: return <RestaurantMenu />;
  }
};

const ManageMeals = () => {
  const theme = useTheme();
  const [meals, setMeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [editingMeal, setEditingMeal] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [preferences, setPreferences] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [csvUploadLoading, setCsvUploadLoading] = useState(false);
  const [confirmCsvDelete, setConfirmCsvDelete] = useState(false);
  const [csvDeleteLoading, setCsvDeleteLoading] = useState(false);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10); // Number of items per page
  const [totalMeals, setTotalMeals] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const { control, register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      name: '',
      description: '',
      mealType: '',
      menuType:'',
      dietaryType: [],
      cuisineStyle: [],
      seasonality: [],
      imageUrl: ''
    }
  });

  useEffect(() => {
    fetchMeals(page, limit);
    fetchPreferences();
  }, [page, limit]);

  const fetchMeals = async (pageNum = page, pageLimit = limit) => {
    try {
      setLoading(true);
      try {
        // Use search API with pagination parameters
        const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.168.68:5000/api';
        const token = localStorage.getItem('token');
        
        // Construct the URL with pagination parameters
        const url = `${baseUrl}/admin/meals?page=${pageNum}&limit=${pageLimit}`;
        
        const response = await fetch(url, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch meals');
        }
        
        const data = await response.json();

        console.log("dataaaaaaaa",data);
        
        // Update state with paginated meals and pagination metadata
        setMeals(data.meals || []);
        setTotalMeals(data.pagination?.total || data.meals?.length || 0);
        setTotalPages(data.pagination?.pages || Math.ceil((data.pagination?.total || data.meals?.length) / pageLimit) || 1);
        
      } catch (err) {
        console.log('Using mock data for meals', err);
        // Mock data for development
        const mockMeals = [
          {
            _id: '1',
            name: 'Masala Dosa',
            description: 'South Indian crispy crepe filled with spiced potatoes',
            mealType: 'Breakfast',
            dietaryType: ['Vegetarian'],
            cuisineStyle: ['Indian', 'South Indian'],
            seasonality: ['Any'],
            imageUrl: 'https://example.com/masala-dosa.jpg'
          },
          {
            _id: '2',
            name: 'Palak Paneer',
            description: 'Cottage cheese cubes in spinach gravy',
            mealType: 'Dinner',
            dietaryType: ['Vegetarian'],
            cuisineStyle: ['Indian', 'North Indian'],
            seasonality: ['Winter'],
            imageUrl: 'https://example.com/palak-paneer.jpg'
          },
          {
            _id: '3',
            name: 'Chicken Tikka Masala',
            description: 'Grilled chicken chunks in spiced curry sauce',
            mealType: 'Dinner',
            dietaryType: ['Non-Vegetarian'],
            cuisineStyle: ['Indian'],
            seasonality: ['Any'],
            imageUrl: 'https://example.com/chicken-tikka.jpg'
          },
          {
            _id: '4',
            name: 'Butter Naan',
            description: 'Buttery Indian bread',
            mealType: 'Dinner',
            dietaryType: ['Vegetarian'],
            cuisineStyle: ['Indian', 'North Indian'],
            seasonality: ['Any'],
            imageUrl: 'https://example.com/butter-naan.jpg'
          },
          {
            _id: '5',
            name: 'Chole Bhature',
            description: 'Spicy chickpea curry with fried bread',
            mealType: 'Lunch',
            dietaryType: ['Vegetarian'],
            cuisineStyle: ['Indian', 'North Indian'],
            seasonality: ['Any'],
            imageUrl: 'https://example.com/chole-bhature.jpg'
          }
        ];
        
        // Calculate start and end index for the current page
        const startIndex = (pageNum - 1) * pageLimit;
        const endIndex = Math.min(startIndex + pageLimit, mockMeals.length);
        
        // Get meals for current page
        const paginatedMeals = mockMeals.slice(startIndex, endIndex);
        
        setMeals(paginatedMeals);
        setTotalMeals(mockMeals.length);
        setTotalPages(Math.ceil(mockMeals.length / pageLimit));
      }
    } catch (error) {
      console.error('Error fetching meals:', error);
      setNotification({
        open: true,
        message: 'Failed to load meals',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPreferences = async () => {
    try {
      // Set up base URL
      const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.168.68:5000/api';
      const token = localStorage.getItem('token');
      const headers = {
        'Authorization': `Bearer ${token}`
      };
      
      // Try to get all preferences from the preferences API
      try {
        // Fetch all preferences at once
        const response = await fetch(`${baseUrl}/preferences`, {
          headers
        });
        
        if (!response.ok) {
          throw new Error('Failed to fetch preferences');
        }
        
        const allPreferences = await response.json();

        console.log("allPreferences",allPreferences)
        
        // Organize preferences by type
        const organizedPreferences = {
          mealTypes: allPreferences
            .filter(pref => pref.type === 'mealType' && pref.active !== false)
            .map(pref => pref.name)
            .sort(),
          menuType: allPreferences
            .filter(pref => pref.type === 'menuType' && pref.active !== false)
            .map(pref => pref.name)
            .sort(),
          dietaryTypes: allPreferences
            .filter(pref => pref.type === 'dietaryType' && pref.active !== false)
            .map(pref => pref.name)
            .sort(),
          cuisineStyles: allPreferences
            .filter(pref => pref.type === 'cuisineStyle' && pref.active !== false)
            .map(pref => pref.name)
            .sort(),
          seasons: allPreferences
            .filter(pref => pref.type === 'season' && pref.active !== false)
            .map(pref => pref.name)
            .sort()
        };
        
        // Fall back to default values if any category is empty
        if (organizedPreferences.mealTypes.length === 0) {
          organizedPreferences.mealTypes = ['Breakfast', 'Lunch', 'Dinner'];
        }
        if (organizedPreferences.menuType.length === 0) {
          organizedPreferences.menuType = ['Daily', 'Special'];
        }
        if (organizedPreferences.dietaryTypes.length === 0) {
          organizedPreferences.dietaryTypes = ['Vegetarian', 'Non-Vegetarian'];
        }
        if (organizedPreferences.cuisineStyles.length === 0) {
          organizedPreferences.cuisineStyles = ['Indian', 'Chinese', 'Italian'];
        }
        if (organizedPreferences.seasons.length === 0) {
          organizedPreferences.seasons = ['Summer', 'Winter', 'Any'];
        }
        
        console.log('Loaded dynamic preferences:', organizedPreferences);
        setPreferences(organizedPreferences);
        
      } catch (err) {
        console.error('Error fetching dynamic preferences:', err);
        
        // Fall back to alternative API method
        try {
          const response = await adminMealsAPI.getPreferences();
          setPreferences(response.data);
        } catch (apiErr) {
          console.log('Using mock data for preferences');
          // Mock data for development as last resort
          setPreferences({
            mealTypes: ['Breakfast', 'Lunch', 'Dinner', 'Snack'],
            menuType: ['Daily', 'Special'],
            dietaryTypes: ['Vegetarian', 'Non-Vegetarian', 'Vegan', 'Gluten-Free'],
            cuisineStyles: ['Indian', 'Italian', 'Chinese', 'Mexican', 'Healthy'],
            seasons: ['Summer', 'Winter', 'Spring', 'Monsoon', 'Festival']
          });
        }
      }
    } catch (error) {
      console.error('Error fetching preferences:', error);
    }
  };

  // Direct API call function that bypasses the api service layer
  const directApiCall = async (url, method, data) => {
    const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.168.68:5000/api';
    const token = localStorage.getItem('token');
    
    console.log(`Making direct ${method} request to ${url} with token ${token ? 'present' : 'missing'}`);
    console.log('Request data:', data);
    
    // Convert data for API compatibility if needed
    const sanitizedData = data ? {
      ...data,
      // Ensure these are lowercase strings for API validation
      mealType: data.mealType?.toLowerCase?.() || data.mealType,
      // Convert arrays to lowercase if needed
      dietaryType: Array.isArray(data.dietaryType) 
        ? data.dietaryType.map(type => type?.toLowerCase?.() || type) 
        : data.dietaryType,
      seasonality: Array.isArray(data.seasonality) 
        ? data.seasonality.map(season => season?.toLowerCase?.() || season) 
        : data.seasonality
    } : undefined;
    
    console.log('Sanitized data for API:', sanitizedData);
    
    const response = await fetch(`${baseUrl}${url}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: sanitizedData ? JSON.stringify(sanitizedData) : undefined
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      console.error('API error response:', errorText);
      throw new Error(`API Error: ${response.status} ${errorText}`);
    }
    
    return await response.json();
  };
  
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setUploadLoading(true);
      
      // Create form data
      const formData = new FormData();
      formData.append('image', file);
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        setNotification({
          open: true,
          message: 'You must be logged in as an admin to upload images',
          severity: 'error'
        });
        return;
      }
      
      // Upload image
      const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.168.68:5000/api';
      const response = await fetch(`${baseUrl}/upload/image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Upload failed: ${errorText}`);
      }
      
      const result = await response.json();
      
      // Set image URL to form
      const imageUrl = result.file.url;
      setImagePreview(imageUrl);
      
      // Update form
      const fieldValue = {
        target: { name: 'imageUrl', value: imageUrl }
      };
      register('imageUrl').onChange(fieldValue);
      
      setNotification({
        open: true,
        message: 'Image uploaded successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to upload image',
        severity: 'error'
      });
    } finally {
      setUploadLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };
  
  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(1); // Reset to first page when changing items per page
  };

  const onSubmit = async (data) => {
    try {
      console.log('Form submission data:', data);
      setSaveLoading(true);
      
      // Check if user is authenticated before proceeding
      const token = localStorage.getItem('token');
      if (!token) {
        setNotification({
          open: true,
          message: 'You must be logged in as an admin to perform this action',
          severity: 'error'
        });
        return;
      }
      
      // Prepare data, ensuring arrays are properly formatted and handling name-based preferences
      // The API requires lowercase values, but our UI shows user-friendly names
      const formattedData = {
        ...data,
        // Convert arrays and ensure proper format
        mealType: data.mealType,
        dietaryType: Array.isArray(data.dietaryType) ? data.dietaryType : [data.dietaryType].filter(Boolean),
        cuisineStyle: Array.isArray(data.cuisineStyle) ? data.cuisineStyle : [data.cuisineStyle].filter(Boolean),
        tags: Array.isArray(data.tags) ? data.tags : (data.tags ? [data.tags] : []),
        seasonality: Array.isArray(data.seasonality) ? data.seasonality : [data.seasonality].filter(Boolean),
        // Ensure image URL is properly formatted
        imageUrl: data.imageUrl || null
      };
      
      console.log('Prepared meal data for submission:', formattedData);
      
      if (editingMeal) {
        // Update existing meal
        try {
          console.log(`Updating meal ${editingMeal._id} with:`, formattedData);
          
          // Try direct API call first
          try {
            const responseData = await directApiCall(`/admin/meals/${editingMeal._id}`, 'PUT', formattedData);
            console.log('Direct update meal response:', responseData);
            
            // Update in our local array
            setMeals(prev => 
              prev.map(meal => meal._id === editingMeal._id ? { ...meal, ...formattedData } : meal)
            );
            
            setNotification({
              open: true,
              message: `Successfully updated ${formattedData.name}`,
              severity: 'success'
            });
          } catch (directErr) {
            console.error('Direct API call failed, falling back to adminMealsAPI:', directErr);
            // Fall back to regular API
            const response = await adminMealsAPI.updateMeal(editingMeal._id, formattedData);
            
            // Update in our local array
            setMeals(prev => 
              prev.map(meal => meal._id === editingMeal._id ? { ...meal, ...formattedData } : meal)
            );
            
            setNotification({
              open: true,
              message: `Successfully updated ${formattedData.name}`,
              severity: 'success'
            });
          }
        } catch (err) {
          console.error('Error updating meal:', err);
          // Still update UI in development for better experience
          if (process.env.NODE_ENV === 'development') {
            console.log('Mock update meal:', formattedData);
            setMeals(prev => 
              prev.map(meal => meal._id === editingMeal._id ? { ...meal, ...formattedData } : meal)
            );
            setNotification({
              open: true,
              message: `Successfully updated ${formattedData.name} (Mock)`,
              severity: 'success'
            });
          } else {
            throw err; // Re-throw to be caught by outer catch block
          }
        }
      } else {
        // Add new meal using direct API call to bypass any middleware issues
        try {
          console.log('Adding new meal with data:', formattedData);
          
          try {
            // Use direct fetch API call first
            const responseData = await directApiCall('/admin/meals', 'POST', formattedData);
            console.log('Direct add meal response:', responseData);
            
            // Add the newly created meal to our state
            const newMeal = responseData;
            setMeals(prev => [...prev, newMeal]);
            
            setNotification({
              open: true,
              message: `Successfully added ${formattedData.name}`,
              severity: 'success'
            });
          } catch (directErr) {
            console.error('Direct API call failed, falling back to adminMealsAPI:', directErr);
            
            // Fall back to regular API
            const response = await adminMealsAPI.addMeal(formattedData);
            console.log('Add meal response:', response);
            
            // Add the newly created meal to our state
            const newMeal = response.data;
            setMeals(prev => [...prev, newMeal]);
            
            setNotification({
              open: true,
              message: `Successfully added ${formattedData.name}`,
              severity: 'success'
            });
          }
        } catch (err) {
          console.error('Error adding meal:', err);
          
          // Still update UI in development for better experience
          if (process.env.NODE_ENV === 'development') {
            console.log('Mock add meal:', formattedData);
            const newMeal = {
              _id: String(Date.now()),
              ...formattedData
            };
            setMeals(prev => [...prev, newMeal]);
            setNotification({
              open: true,
              message: `Successfully added ${formattedData.name} (Mock)`,
              severity: 'info'
            });
          } else {
            throw err; // Re-throw to be caught by outer catch block
          }
        }
      }
      
      // Reset form and state
      reset();
      setShowForm(false);
      setEditingMeal(null);
    } catch (error) {
      console.error('Error saving meal:', error);
      setNotification({
        open: true,
        message: error.response?.data?.message || 'Failed to save meal. Please check your connection and try again.',
        severity: 'error'
      });
    } finally {
      setSaveLoading(false);
    }
  };

  const handleEdit = (meal) => {
    setEditingMeal(meal);
    setShowForm(true);
    // Set image preview if available
    setImagePreview(meal.imageUrl || null);
    reset({
      name: meal.name || '',
      description: meal.description || '',
      mealType: meal.mealType || '',
      dietaryType: meal.dietaryType || [],
      cuisineStyle: meal.cuisineStyle || [],
      seasonality: meal.seasonality || [],
      tags: meal.tags || [], 
      menuType: meal.menuType || '',
      imageUrl: meal.imageUrl || ''
    });
  };

  const handleDeleteClick = (mealId) => {
    const mealToDelete = meals.find(meal => meal._id === mealId);
    if (mealToDelete) {
      setConfirmDelete(mealToDelete);
    }
  };
  
  const handleDelete = async () => {
    if (!confirmDelete) return;
    
    try {
      try {
        await adminMealsAPI.deleteMeal(confirmDelete._id);
      } catch (err) {
        console.log('Mock delete meal:', confirmDelete._id);
        // Remove from our mock array
        setMeals(prev => prev.filter(meal => meal._id !== confirmDelete._id));
      }
      
      setNotification({
        open: true,
        message: `${confirmDelete.name} has been deleted`,
        severity: 'success'
      });
      setConfirmDelete(null);
    } catch (error) {
      console.error('Error deleting meal:', error);
      setNotification({
        open: true,
        message: 'Failed to delete meal',
        severity: 'error'
      });
    }
  };

  if (loading || !preferences) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleCloseNotification = () => {
    setNotification({ ...notification, open: false });
  };

  const handleCancelDelete = () => {
    setConfirmDelete(null);
  };

  // Handle CSV file upload
  const handleCsvUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    
    try {
      setCsvUploadLoading(true);
      
      // Create form data
      const formData = new FormData();
      formData.append('file', file);
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        setNotification({
          open: true,
          message: 'You must be logged in as an admin to upload CSV',
          severity: 'error'
        });
        return;
      }
      
      // Upload CSV file
      const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.168.68:5000/api';
      const response = await fetch(`${baseUrl}/upload/meals/csv`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to upload CSV file');
      }
      
      setNotification({
        open: true,
        message: `CSV processed: ${result.inserted} meals added, ${result.failed} failed`,
        severity: result.failed > 0 ? 'warning' : 'success'
      });
      
      // Refresh meals list
      fetchMeals();
      
    } catch (error) {
      console.error('Error uploading CSV:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to upload CSV file',
        severity: 'error'
      });
    } finally {
      setCsvUploadLoading(false);
      // Clear the file input
      event.target.value = '';
    }
  };
  
  // Handle CSV delete confirmation
  const handleConfirmCsvDelete = () => {
    setConfirmCsvDelete(true);
  };
  
  // Handle CSV delete cancellation
  const handleCancelCsvDelete = () => {
    setConfirmCsvDelete(false);
  };
  
  // Handle CSV delete action
  const handleCsvDelete = async () => {
    try {
      setCsvDeleteLoading(true);
      
      // Get auth token
      const token = localStorage.getItem('token');
      if (!token) {
        setNotification({
          open: true,
          message: 'You must be logged in as an admin to delete CSV uploads',
          severity: 'error'
        });
        return;
      }
      
      // Delete CSV uploaded meals
      const baseUrl = process.env.REACT_APP_API_URL || 'http://192.168.168.68:5000/api';
      const response = await fetch(`${baseUrl}/upload/meals/csv`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Failed to delete CSV uploads');
      }
      
      setNotification({
        open: true,
        message: `Successfully deleted ${result.deletedCount} CSV uploaded meals`,
        severity: 'success'
      });
      
      // Refresh meals list
      fetchMeals();
      
    } catch (error) {
      console.error('Error deleting CSV uploads:', error);
      setNotification({
        open: true,
        message: error.message || 'Failed to delete CSV uploads',
        severity: 'error'
      });
    } finally {
      setCsvDeleteLoading(false);
      setConfirmCsvDelete(false);
    }
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h3" component="h1" fontWeight="bold" color="primary" gutterBottom>
            Manage Meal Ideas
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Add, edit, or remove meal ideas with associated filters and tags
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {/* CSV Upload Button */}
          <Button
            component="label"
            variant="outlined"
            color="primary"
            startIcon={<CloudUploadIcon />}
            disabled={csvUploadLoading}
            size="large"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {csvUploadLoading ? 'Uploading...' : 'Upload CSV'}
            <input
              type="file"
              hidden
              accept=".csv"
              onChange={handleCsvUpload}
            />
          </Button>
          
          {/* Delete CSV Uploads Button */}
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={handleConfirmCsvDelete}
            disabled={csvDeleteLoading}
            size="large"
            sx={{ whiteSpace: 'nowrap' }}
          >
            {csvDeleteLoading ? 'Deleting...' : 'Delete CSV Uploads'}
          </Button>
          
          {/* Add New Meal Button */}
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setEditingMeal(null);
              setShowForm(true);
              setImagePreview(null);
              reset({
                name: '',
                description: '',
                mealType: '',
                menuType: '',
                dietaryType: [],
                cuisineStyle: [],
                seasonality: [],
                imageUrl: ''
              });
            }}
            size="large"
          >
            Add New Meal
          </Button>
        </Box>
      </Box>

      <Dialog
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setEditingMeal(null);
          reset();
          setImagePreview(null);
        }}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {editingMeal ? <EditIcon sx={{ mr: 1 }} /> : <AddIcon sx={{ mr: 1 }} />}
            {editingMeal ? 'Edit Meal' : 'Add New Meal'}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 2 }}>
            <Grid container spacing={3} >
              <Grid item xs={12} sm={8} md={6 }>
                <TextField
                  label="Meal Name"
                  fullWidth
                  required
                  {...register('name')}
                  error={Boolean(errors.name)}
                  helperText={errors.name?.message}
                  variant="outlined"
                />
              </Grid>
              
              <Grid item xs={12} sm={4} md={6}>
                <FormControl fullWidth required error={Boolean(errors.mealType)}>
                  <InputLabel id="meal-type-label">Meal Type</InputLabel>
                  <Controller
                    name="mealType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="meal-type-label"
                        label="Meal Type"
                      >
                        {preferences.mealTypes.map(type => (
                          <MenuItem key={type} value={type}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ mr: 1 }}>{getMealTypeIcon(type)}</Box>
                              {type}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.mealType && <FormHelperText>{errors.mealType.message}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <TextField
                  label="Description"
                  fullWidth
                  multiline
                  rows={3}
                  {...register('description')}
                  variant="outlined"
                />
              </Grid>

              <Grid item xs={12} sm={4} md={6}>
                <FormControl fullWidth required error={Boolean(errors.mealType)}>
                  <InputLabel id="meal-type-label">Menu Type</InputLabel>
                  <Controller
                    name="menuType"
                    control={control}
                    render={({ field }) => (
                      <Select
                        {...field}
                        labelId="menu-type-label"
                        label="Menu Type"
                      >
                        {preferences.menuType.map(type => (
                          <MenuItem key={type} value={type}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                              <Box sx={{ mr: 1 }}>{getMealIcon(type)}</Box>
                              {type}
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  />
                  {errors.mealType && <FormHelperText>{errors.mealType.message}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="subtitle1" gutterBottom>Image</Typography>
                  
                  {/* Image preview */}
                  {imagePreview && (
                    <Box sx={{ mb: 2, textAlign: 'center' }}>
                      <img 
                        src={imagePreview} 
                        alt="Meal preview" 
                        style={{ 
                          maxWidth: '100%', 
                          maxHeight: '200px', 
                          objectFit: 'contain',
                          borderRadius: '8px',
                          border: '1px solid #ddd'
                        }} 
                      />
                    </Box>
                  )}
                  
                  <Grid container spacing={2}>
                    {/* File Upload */}
                    <Grid item xs={12} sm={6}>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ width: '100%', py: 1.5 }}
                        disabled={uploadLoading}
                      >
                        {uploadLoading ? 'Uploading...' : 'Upload Image'}
                        <input
                          type="file"
                          accept="image/*"
                          hidden
                          onChange={handleImageUpload}
                        />
                      </Button>
                    </Grid>
                    
                    {/* URL Input */}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="or Enter Image URL"
                        fullWidth
                        {...register('imageUrl')}
                        error={Boolean(errors.imageUrl)}
                        helperText={errors.imageUrl?.message}
                        variant="outlined"
                        onChange={(e) => {
                          register('imageUrl').onChange(e);
                          if (e.target.value) {
                            setImagePreview(e.target.value);
                          }
                        }}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" sx={{ width: '100%' }} error={Boolean(errors.dietaryType)}>
                  <FormLabel component="legend">Dietary Type</FormLabel>
                  <FormGroup row>
                    {preferences.dietaryTypes.map(type => (
                      <FormControlLabel
                        key={type}
                        control={
                          <Controller
                            name="dietaryType"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={field.value.includes(type)}
                                onChange={(e) => {
                                  const updatedValue = e.target.checked
                                    ? [...field.value, type]
                                    : field.value.filter(val => val !== type);
                                  field.onChange(updatedValue);
                                }}
                              />
                            )}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ mr: 0.5 }}>{getDietaryTypeIcon(type)}</Box>
                            {type}
                          </Box>
                        }
                      />
                    ))}
                  </FormGroup>
                  {errors.dietaryType && <FormHelperText>{errors.dietaryType.message}</FormHelperText>}
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend">Cuisine Style</FormLabel>
                  <FormGroup>
                    {preferences.cuisineStyles.map(style => (
                      <FormControlLabel
                        key={style}
                        control={
                          <Controller
                            name="cuisineStyle"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={field.value.includes(style)}
                                onChange={(e) => {
                                  const updatedValue = e.target.checked
                                    ? [...field.value, style]
                                    : field.value.filter(val => val !== style);
                                  field.onChange(updatedValue);
                                }}
                              />
                            )}
                          />
                        }
                        label={style}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
              
              <Grid item xs={12} md={6}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                  <FormLabel component="legend">Seasonality</FormLabel>
                  <FormGroup>
                    {preferences.seasons.map(season => (
                      <FormControlLabel
                        key={season}
                        control={
                          <Controller
                            name="seasonality"
                            control={control}
                            render={({ field }) => (
                              <Checkbox
                                checked={field.value.includes(season)}
                                onChange={(e) => {
                                  const updatedValue = e.target.checked
                                    ? [...field.value, season]
                                    : field.value.filter(val => val !== season);
                                  field.onChange(updatedValue);
                                }}
                              />
                            )}
                          />
                        }
                        label={season}
                      />
                    ))}
                  </FormGroup>
                </FormControl>
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowForm(false);
              setEditingMeal(null);
              reset();
              setImagePreview(null);
            }}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit(onSubmit)}
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
          >
            {editingMeal ? 'Update Meal' : 'Add Meal'}
          </Button>
        </DialogActions>
      </Dialog>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, width: '100%' }}>
        {meals.map(meal => (
          <Box 
            key={meal._id} 
            sx={{ 
              width: { xs: '100%', sm: 'calc(50% - 8px)', md: 'calc(33.33% - 11px)' },
              display: 'flex'
            }}
          >
            <Card elevation={2} sx={{ width: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'translateY(-4px)' } }}>
              {/* Add image to card if available */}
              {meal.imageUrl && (
                <CardMedia
                  component="img"
                  height="160"
                  image={meal.imageUrl}
                  alt={meal.name}
                  onError={(e) => {
                    e.target.onerror = null; 
                    e.target.src = 'https://via.placeholder.com/300x160?text=Image+Not+Available';
                  }}
                />
              )}
              <CardHeader
                title={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box component="span" sx={{ mr: 1 }}>
                      {getMealTypeIcon(meal.mealType)}
                    </Box>
                    <Typography variant="h6" component="span" noWrap>
                      {meal.name}
                    </Typography>
                  </Box>
                }
                subheader={meal.mealType}
                action={
                  <Box>
                    <Tooltip title="Edit">
                      <IconButton
                        size="small"
                        onClick={() => handleEdit(meal)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteClick(meal._id)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                }
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderBottom: 1,
                  borderColor: 'divider'
                }}
              />
              
              <CardContent sx={{ flexGrow: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }} gutterBottom>
                  {meal.description || 'No description provided'}
                </Typography>
                
                <Box sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle2" color="text.primary" gutterBottom>
                    Dietary:
                  </Typography>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {meal.dietaryType?.map(type => (
                      <Chip
                        key={type}
                        size="small"
                        label={type}
                        icon={getDietaryTypeIcon(type)}
                        variant="outlined"
                        sx={{ mb: 0.5 }}
                      />
                    ))}
                  </Stack>
                </Box>
                
                {meal.cuisineStyle?.length > 0 && (
                  <Box sx={{ mb: 1.5 }}>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Cuisine:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {meal.cuisineStyle.map(style => (
                        <Chip
                          key={style}
                          size="small"
                          label={style}
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
                
                {meal.seasonality?.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.primary" gutterBottom>
                      Season:
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap">
                      {meal.seasonality.map(season => (
                        <Chip
                          key={season}
                          size="small"
                          label={season}
                          variant="outlined"
                          sx={{ mb: 0.5 }}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Box>
        ))}
        
        {meals.length === 0 && (
          <Box sx={{ width: '100%' }}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                textAlign: 'center',
                bgcolor: alpha(theme.palette.info.light, 0.1),
                borderRadius: 2
              }}
            >
              <Typography variant="body1" color="text.secondary">
                No meal ideas found. Add your first meal to get started!
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingMeal(null);
                  setShowForm(true);
                  setImagePreview(null);
                  reset();
                }}
                sx={{ mt: 2 }}
              >
                Add Meal Idea
              </Button>
            </Paper>
          </Box>
        )}
      </Box>
      
      {/* Pagination Controls */}
      {meals.length > 0 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 4, flexWrap: 'wrap', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2" component="span" mr={2}>
              Items per page:
            </Typography>
            <Select
              value={limit}
              onChange={handleLimitChange}
              size="small"
              variant="outlined"
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={30}>30</MenuItem>
              <MenuItem value={50}>50</MenuItem>
            </Select>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography variant="body2" component="span" mr={2}>
              Showing {meals.length} of {totalMeals} meals
            </Typography>
          </Box>
          
          <Box>
            <Pagination 
              count={totalPages} 
              page={page} 
              onChange={handlePageChange} 
              color="primary" 
              showFirstButton 
              showLastButton
              size="large"
            />
          </Box>
        </Box>
      )}
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={Boolean(confirmDelete)}
        onClose={handleCancelDelete}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{confirmDelete?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            Cancel
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained" startIcon={<DeleteIcon />}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Delete Confirmation Dialog */}
      <Dialog
        open={confirmCsvDelete}
        onClose={handleCancelCsvDelete}
      >
        <DialogTitle>Confirm CSV Deletion</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>all CSV uploaded meals</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelCsvDelete}>
            Cancel
          </Button>
          <Button 
            onClick={handleCsvDelete} 
            color="error" 
            variant="contained" 
            startIcon={<DeleteIcon />}
            disabled={csvDeleteLoading}
          >
            {csvDeleteLoading ? 'Deleting...' : 'Delete All CSV Meals'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={5000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ManageMeals;
